import { AxisDomain } from 'recharts/types/util/types'
import { ChartValues, PowerPrice } from '../types'
import moment from '@e3dc-react/shell/Libs/moment'

const MAX_PRICE_LIMIT_DIFFERENCE = 40
const OFFSET = 10
const MAX_OFFSET = 6

/**
 * Transforms power price data (which can include gaps and ranges larger than one hour) to hourly data
 * @param powerPrices - power price data
 * @param to - date string until which prices are needed
 */
export const transformPowerPriceToHourValues = (powerPrices: PowerPrice[], to: string): ChartValues[] => {
  if (powerPrices.length === 0) return []

  const chartValues: ChartValues[] = []
  const end = new Date(to)
  let currentTime = new Date(powerPrices[0].validFrom)

  while (currentTime <= end) {
    const time = new Date(currentTime.getTime())
    const priceItem = powerPrices.find(item => time.toISOString() >= item.validFrom && time.toISOString() < item.validTo)

    const incrementedTime = new Date(currentTime.setMinutes(currentTime.getMinutes() + 15))

    chartValues.push({
      hour: moment(time).format('HH:mm'),
      from: time.toISOString(),
      to: incrementedTime.toISOString(),
      price: priceItem ? parseFloat(priceItem.price.toFixed(3)) : null,
    })

    currentTime = incrementedTime
  }

  return chartValues
}

/**
 * convert api data to data that can be used for the chart
 * @param values
 * @returns converted ChartValues
 */
export const getChartPowerPriceValues = (values: PowerPrice[]): ChartValues[] => {
  const chartValues: ChartValues[] = values.map((value, index, arr) => {
    const fromHour = moment(value.validFrom).format('HH:mm')

    return {
      hour: fromHour,
      from: value.validFrom,
      to: value.validTo,
      price: parseFloat(value.price.toFixed(3)),
    }
  })

  chartValues.push(addEmptyDataPoint(chartValues))

  return chartValues
}

/**
 * function to add an empty data point to the chart, we need this to display data from 00:00 to 00:00
 */
const addEmptyDataPoint = (chartValues: ChartValues[]): ChartValues => {
  return {
    ...chartValues[chartValues.length - 1],
    hour: moment(chartValues[chartValues.length - 1]?.from)
      .add(1, 'hour')
      .format('HH:mm'),
    isEmptyPoint: true,
  }
}

/**
 * Function to get the current power price
 *
 * @returns current power price with validity of one hour, or undefined
 */
export const getCurrentPowerPrice = (powerPrices: PowerPrice[]): PowerPrice | undefined => {
  const currentDate = new Date()
  const currentPrice = powerPrices.find(value => new Date(value.validFrom) <= currentDate && new Date(value.validTo) >= currentDate)

  if (!currentPrice) return

  const { from, to } = getCurrentsPriceDateRange(currentDate)

  const currentQuarterHourPrice: PowerPrice = {
    ...currentPrice,
    validFrom: from.toISOString(),
    validTo: to.toISOString(),
  }
  return currentQuarterHourPrice
}

/**
 * Helper function to determine the date range for the current price.
 * Range is always 15min.
 */
const getCurrentsPriceDateRange = (currentDate: Date): { from: Date; to: Date } => {
  const currentMinutes = currentDate.getMinutes()
  const roundedMinutes = Math.floor(currentMinutes / 15) * 15

  const from = new Date(currentDate)
  from.setMinutes(roundedMinutes, 0, 0)

  const to = new Date(from)
  to.setMinutes(from.getMinutes() + 15)

  return { from, to }
}

/**
 * function to check if power prices include any negative value
 * @param values
 * @returns boolean
 */
export const checkForNegativePrices = (values: PowerPrice[]): boolean => {
  const negativePrice = values.find(value => value.price < 0)

  return !!negativePrice
}

/**
 * This function calculates the y-position for a reference line in a chart.
 *
 * @param {number} limit - The price limit value.
 * @param {number} maxPrice - The maximum price value in the powerprices.
 * @param {boolean} downMd - A flag indicating whether the screen size is medium down (responsive design).
 * @param {boolean} isModal - A flag indicating whether the chart is displayed in a modal.
 *
 * The function  checks if the difference between the `limit` and `maxPrice` is greater than a predefined constant `MAX_PRICE_LIMIT_DIFFERENCE`.
 * If it is, the function returns the `maxPrice` plus `MAX_PRICE_LIMIT_DIFFERENCE`, minus the calculated padding top.
 * If it's not, the function returns the `limit` minus a default padding top (which is 0 if the chart is in a modal).
 *
 * @returns {number} The calculated y-position for the reference line.
 */
export const getReferenceLineYPosition = (limit: number, minPrice: number, maxPrice: number, downMd?: boolean, isModal?: boolean): number => {
  /* if limit is negative than the reference line should stay at 0 */
  if (limit < 0) return 0
  /* else if limit is less than the minPrice the line can stay as it is */
  if (limit < minPrice) return limit

  const defaultPaddingTop = !isModal && limit > maxPrice ? 5 : 0
  let paddingTop = 0

  if (!isModal) {
    paddingTop = downMd ? 16 : 14
  } else {
    paddingTop = 5
  }

  return limit - maxPrice > MAX_PRICE_LIMIT_DIFFERENCE ? maxPrice + MAX_PRICE_LIMIT_DIFFERENCE - paddingTop : limit - defaultPaddingTop
}

/**
 * Calculates the Y-axis domain for a chart based on provided limits and activity flags.
 */
export const getChartYAxisDomain = (
  min: number,
  max: number,
  wallboxLimit: number,
  batteryLimit: number,
  isDynamicPowerPriceEnabledForWallboxes: boolean,
  isDynamicPowerPriceEnabledForBattery: boolean
): AxisDomain => {
  let limit = max
  let maxDomain = max
  let minDomain = 0

  if (isDynamicPowerPriceEnabledForWallboxes && isDynamicPowerPriceEnabledForBattery) {
    limit = Math.max(wallboxLimit, batteryLimit)
    minDomain = Math.min(0, min, wallboxLimit, batteryLimit)
  } else if (isDynamicPowerPriceEnabledForWallboxes) {
    limit = wallboxLimit
    minDomain = Math.min(0, min, wallboxLimit)
  } else if (isDynamicPowerPriceEnabledForBattery) {
    limit = batteryLimit
    minDomain = Math.min(0, min, batteryLimit)
  }

  if (limit > max) {
    maxDomain = limit - max > MAX_PRICE_LIMIT_DIFFERENCE ? max + MAX_PRICE_LIMIT_DIFFERENCE : limit
  }

  if (min < 0) {
    minDomain = min
  }

  if (minDomain > maxDomain) {
    console.error(
      'y axis domain is calculated where min is bigger than max. That is not allowed and could lead to problems within the chart. Domain fallbacks to automatic calculation.'
    )
    return [0, 'auto']
  }
  return calculateDomainRangeWithOffset(minDomain, maxDomain)
}

/**
 * Calculates offset for padding based on the domain rang
 */
export const calculateDomainRangeWithOffset = (minDomain: number, maxDomain: number): AxisDomain => {
  let maxDomainOffset = maxDomain > 0 ? maxDomain / OFFSET : maxDomain / -OFFSET
  let minDomainOffset = minDomain > 0 ? minDomain / -OFFSET : minDomain / OFFSET

  if (maxDomainOffset > MAX_OFFSET) {
    maxDomainOffset = MAX_OFFSET
  }
  if (minDomainOffset < -MAX_OFFSET) {
    minDomainOffset = -MAX_OFFSET
  }

  const adjustedMaxDomain = maxDomain + maxDomainOffset
  const adjustedMinDomain = minDomain + minDomainOffset

  return [adjustedMinDomain, adjustedMaxDomain]
}
